import React from 'react';
import MarkdownText from '../components/MarkdownText';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';
import apiService from '../services/ApiService';

/**
 * Wrapper component to provide hooks to class component
 */
function DocumentPageWrapper() {
  const params = useParams();
  const navigate = useNavigate();
  return <DocumentPage id={params.id} navigate={navigate} />;
}

/**
 * Document page component that shows a single document
 */
class DocumentPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      document: null,
      loading: true,
      error: null,
      deleteDialogOpen: false,
      deleteConfirmationPhrase: 'delete this document',
      userDeleteConfirmation: '',
      deleteConfirmationError: false
    };
    
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
    this.handleDeleteCancel = this.handleDeleteCancel.bind(this);
    this.handleConfirmationInputChange = this.handleConfirmationInputChange.bind(this);
  }

  /**
   * Fetch document data when component mounts
   */
  componentDidMount() {
    this.fetchDocument();
  }

  /**
   * Fetch document if ID changes
   * @param {Object} prevProps - Previous props
   */
  componentDidUpdate(prevProps) {
    if (this.props.id !== prevProps.id) {
      this.fetchDocument();
    }
  }

  /**
   * Fetch document from API
   */
  async fetchDocument() {
    this.setState({ loading: true, error: null });
    
    try {
      const response = await apiService.getDocumentById(this.props.id);
      // The response is already structured with success and data properties
      if (response.success && response.data) {
        this.setState({ document: response.data, loading: false });
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error in fetchDocument:', error);
      this.setState({
        error: error.response?.status === 404
          ? 'Document not found'
          : 'Error loading document. Please try again later.',
        loading: false
      });
    }
  }

  /**
   * Format date to readable string
   * @param {String} dateString - ISO date string
   * @returns {String} Formatted date
   */
  formatDate(dateString) {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  }

  /**
   * Format timestamp to readable datetime string
   * @param {Number|String} timestamp - Unix timestamp or ISO date string
   * @returns {String} Formatted datetime
   */
  formatTimestamp(timestamp) {
    let date;
    
    // Handle both _ts (unix timestamp) and ISO date strings
    if (typeof timestamp === 'number') {
      date = new Date(timestamp * 1000); // Convert Unix timestamp to milliseconds
    } else {
      date = new Date(timestamp);
    }
    
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleDateString(undefined, options);
  }

  /**
   * Get document timestamp - prefer _ts over createdAt
   * @param {Object} document - Document object
   * @returns {Number|String} Timestamp
   */
  getDocumentTimestamp(document) {
    return document._ts || document.createdAt;
  }

  /**
   * Open delete confirmation dialog
   */
  handleDeleteClick() {
    this.setState({ 
      deleteDialogOpen: true,
      userDeleteConfirmation: '',
      deleteConfirmationError: false 
    });
  }

  /**
   * Close delete confirmation dialog
   */
  handleDeleteCancel() {
    this.setState({ 
      deleteDialogOpen: false,
      userDeleteConfirmation: '',
      deleteConfirmationError: false
    });
  }
  
  /**
   * Handle confirmation input change
   * @param {Object} event - Input change event
   */
  handleConfirmationInputChange(event) {
    this.setState({ 
      userDeleteConfirmation: event.target.value,
      deleteConfirmationError: false
    });
  }

  /**
   * Delete the document and navigate to home
   */
  async handleDeleteConfirm() {
    const { userDeleteConfirmation, deleteConfirmationPhrase } = this.state;
    
    // Check if user confirmation matches required phrase
    if (userDeleteConfirmation.toLowerCase() !== deleteConfirmationPhrase.toLowerCase()) {
      this.setState({ deleteConfirmationError: true });
      return;
    }
    
    try {
      this.setState({ loading: true, deleteDialogOpen: false });
      const response = await apiService.deleteDocument(this.props.id);
      
      if (response && response.success) {
        // Successfully deleted - navigate home
        this.props.navigate('/');
      } else {
        // Unexpected response format
        this.setState({
          error: 'Failed to delete document due to unexpected server response.',
          loading: false
        });
      }
    } catch (error) {
      console.error('Error in handleDeleteConfirm:', error);
      this.setState({
        error: 'Failed to delete document. Please try again.',
        loading: false
      });
    }
  }

  render() {
    const { document, loading, error, deleteDialogOpen } = this.state;

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!document) {
      return null;
    }

    return (
      <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: { xs: 2, sm: 3 } }}>
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography 
            color="text.primary"
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            <MarkdownText>{document.Term || 'Untitled'}</MarkdownText>
          </Typography>
        </Breadcrumbs>

        {/* Document header */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 },
            mb: 2 
          }}>
            <Typography 
              variant="h4" 
              component="h1"
              sx={{
                color: '#6c0c46',
                fontWeight: 600,
                borderBottom: '2px solid rgba(108, 12, 70, 0.2)',
                paddingBottom: '8px',
                flexGrow: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' },
                wordBreak: 'break-word'
              }}
            >
              <MarkdownText>{document.Term || 'Untitled'}</MarkdownText>
            </Typography>
            
            <Box sx={{ 
              display: 'flex',
              gap: 1,
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                component={RouterLink}
                to={`/documents/${document.id}/edit`}
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
                size="small"
                sx={{ 
                  flex: { xs: 1, sm: 'none' },
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }}
              >
                Edit
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={this.handleDeleteClick}
                size="small"
                sx={{ 
                  flex: { xs: 1, sm: 'none' },
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Document metadata */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {this.formatTimestamp(this.getDocumentTimestamp(document))} • 
            Version: {document.version}
          </Typography>
          
          {document.tags && document.tags.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {document.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  component={RouterLink}
                  to={`/search?tag=${encodeURIComponent(tag)}`}
                  clickable
                  sx={{ 
                    mr: 0.5, 
                    mb: 0.5,
                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                    height: { xs: 20, sm: 24 }
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: { xs: 2, sm: 3 } }} />

        {/* Document content */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            bgcolor: 'background.default',
            borderRadius: { xs: 1, sm: 2 }
          }}
        >
          <Box 
            sx={{ 
              typography: 'body1',
              '& img': { maxWidth: '100%', height: 'auto' },
              '& h1, & h2, & h3, & h4, & h5, & h6': { 
                mt: 2, 
                mb: 1,
                fontWeight: 'bold',
                color: '#6c0c46'
              },
              '& h1': { fontSize: { xs: '1.5rem', sm: '2rem' } },
              '& h2': { fontSize: { xs: '1.3rem', sm: '1.8rem' } },
              '& h3': { fontSize: { xs: '1.2rem', sm: '1.6rem' } },
              '& h4': { fontSize: { xs: '1.1rem', sm: '1.4rem' } },
              '& h5': { fontSize: { xs: '1rem', sm: '1.2rem' } },
              '& h6': { fontSize: { xs: '0.9rem', sm: '1.1rem' } },
              '& p': { mb: 2, lineHeight: 1.6 },
              '& ul, & ol': { mb: 2, pl: 4 },
              '& pre': { 
                backgroundColor: '#f5f5f5',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              },
              '& code': { 
                backgroundColor: '#f5f5f5',
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              },
              '& blockquote': {
                borderLeft: '4px solid #6c0c46',
                pl: 2,
                ml: 0,
                fontStyle: 'italic',
                color: 'text.secondary'
              },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              lineHeight: { xs: 1.5, sm: 1.6 }
            }}
          >
            <MarkdownText inline={false}>{document.Definition || 'No definition available'}</MarkdownText>
          </Box>
        </Paper>

        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={this.handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              This will permanently delete the document "{document.Term || 'Untitled'}". This action cannot be undone.
            </DialogContentText>
            
            <DialogContentText sx={{ mb: 2, fontWeight: 'bold' }}>
              To confirm deletion, please type: "{this.state.deleteConfirmationPhrase}" below.
            </DialogContentText>
            
            <TextField
              autoFocus
              margin="dense"
              label="Confirmation"
              fullWidth
              variant="outlined"
              value={this.state.userDeleteConfirmation}
              onChange={this.handleConfirmationInputChange}
              error={this.state.deleteConfirmationError}
              helperText={this.state.deleteConfirmationError ? 'Please type the confirmation phrase exactly as shown above' : ''}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleDeleteCancel} variant="outlined">Cancel</Button>
            <Button 
              onClick={this.handleDeleteConfirm} 
              color="error" 
              variant="contained"
              disabled={this.state.userDeleteConfirmation.toLowerCase() !== this.state.deleteConfirmationPhrase.toLowerCase()}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default DocumentPageWrapper;
